import { box, type Aabb, type World } from '../sim.js';
import type { Vec3 } from '../math.js';
import { EntityKind } from '../protocol.js';
import type { AreaDef, HymnSpawnDef, MapDef, NpcDef, WaypointGraph } from '../maps.js';
import { buildHeightfield, heightAt, type Heightfield } from '../terrain.js';
import { groundHeightAt, scatter, slabRoom } from './kit.js';
import { DOWNS_GRID, type RegionSidecar } from './region.js';

/**
 * The Vigil Downs - the persistent zone north of Lanternfall's gate, and the emphasis of this
 * slice. X is east, Z is south, y is up, yaw 0 faces -Z (north) - see spine.ts/sim.ts. Roughly
 * 500 x 500 m (x/z -250..250). Gentle rolling downs (mostly 0-8 m), a 25 m rise at the Watch
 * Hill in the north-east corner, a shallow -2 m dip at the Drowned Chapel in the low west, and a
 * flattened road corridor running the Lantern Road north from the south gate. Entry is from the
 * south (z = +250 edge); Lanternfall's own gate is the exit south, and the two maps' exit/spawn
 * numbers are coordinated - see the `exits` entry below and lanternfall.ts's matching one.
 *
 * A danger gradient runs south (weak) to north-east (hardest): Choristers alone near the gate,
 * Choristers with a Cantor through the middle distance, heavier Chorister/Cantor camps around
 * the Drowned Chapel and the Choir Stones, and a Sexton with an escort guarding the Watch Hill.
 *
 * `tools/assetgen/blender/downs_region.py` dresses this ground and exports a sidecar
 * (`maps/region.ts`). Its heightfield is sampled from **this file's own `terrainHeight`**,
 * constant for constant, so the sculpt and the greybox are the same ground and the fallback path
 * is a visual downgrade rather than a different world. Its colliders replace the in-code ones
 * wholesale, because the props it places stand exactly where those boxes did.
 */

// ---------------------------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------------------------

/** Gentle rolling downs, 0-8 m: a fixed sum of sines (no RNG, so it's reproducible). */
function duneNoise(x: number, z: number): number {
  const h =
    4 +
    2.2 * Math.sin(x * 0.012 + z * 0.009) +
    1.4 * Math.sin(x * 0.02 - z * 0.015 + 1.1) +
    0.8 * Math.sin(x * 0.035 + z * 0.03 + 2.2);
  return Math.min(8, Math.max(0, h));
}

/** The Watch Hill: a smooth Gaussian bump in the north-east corner. Peak ~18 m on top of the
 *  ~6-7 m of dune noise already there puts the summit at roughly 25 m, per the brief. */
const WATCH_HILL_X = 180;
const WATCH_HILL_Z = -180;
const WATCH_HILL_PEAK = 18;
const WATCH_HILL_SIGMA = 65;
function watchHillHeight(x: number, z: number): number {
  const dx = x - WATCH_HILL_X;
  const dz = z - WATCH_HILL_Z;
  return (
    WATCH_HILL_PEAK * Math.exp(-(dx * dx + dz * dz) / (2 * WATCH_HILL_SIGMA * WATCH_HILL_SIGMA))
  );
}

/** The Drowned Chapel: a shallow Gaussian bowl in the low west. Depth 2.6 m against the ~0.6 m
 *  of dune noise at its centre lands the floor at roughly -2 m, per the brief. */
const CHAPEL_X = -160;
const CHAPEL_Z = 20;
const CHAPEL_DEPTH = 2.6;
const CHAPEL_SIGMA = 45;
function chapelDip(x: number, z: number): number {
  const dx = x - CHAPEL_X;
  const dz = z - CHAPEL_Z;
  return CHAPEL_DEPTH * Math.exp(-(dx * dx + dz * dz) / (2 * CHAPEL_SIGMA * CHAPEL_SIGMA));
}

/** The Lantern Road corridor + the south gate apron: flattened to y=0 so running it feels good.
 *  Mask fades over `ROAD_MARGIN` m outside the rect, same shape as spine.ts's apron mask. */
const ROAD_RECT = { x0: -9, x1: 9, z0: -230, z1: 255 };
const ROAD_MARGIN = 22;
function roadMask(x: number, z: number): number {
  const dx = x < ROAD_RECT.x0 ? ROAD_RECT.x0 - x : x > ROAD_RECT.x1 ? x - ROAD_RECT.x1 : 0;
  const dz = z < ROAD_RECT.z0 ? ROAD_RECT.z0 - z : z > ROAD_RECT.z1 ? z - ROAD_RECT.z1 : 0;
  const d = Math.hypot(dx, dz);
  return Math.max(0, 1 - d / ROAD_MARGIN);
}

/**
 * Composed terrain. Every term above keeps its own gradient well under the ~0.5 slope budget
 * (the hill's steepest point is peak/sigma*e^-0.5 =~ 0.17, the chapel's =~ 0.04, the dune noise's
 * =~ 0.03), so `stepPlayer`'s STEP_DOWN (0.6 m/tick at 30 Hz) never drops a runner anywhere.
 *
 * Exported because `downs_region.py` is a line-by-line port of it: the Blender sculpt and this
 * function must agree, and a reader comparing the two should be looking at one definition.
 */
export function downsTerrainHeight(x: number, z: number): number {
  const natural = duneNoise(x, z) + watchHillHeight(x, z) - chapelDip(x, z);
  const m = roadMask(x, z);
  return natural * (1 - m);
}

const IN_CODE_TERRAIN: Heightfield = buildHeightfield(
  DOWNS_GRID.originX,
  DOWNS_GRID.originZ,
  DOWNS_GRID.cellSize,
  DOWNS_GRID.cols,
  DOWNS_GRID.rows,
  downsTerrainHeight,
);

// ---------------------------------------------------------------------------------------------
// Fixed content coordinates. The Blender pass builds to exactly these, so a camp, an area or an
// NPC written here still lands on the right stone once the GLB is in.
// ---------------------------------------------------------------------------------------------
const SHRINE_X = 18;
const SHRINE_Z = 40;
const CHOIR_CX = 0;
const CHOIR_CZ = -80;
const CHOIR_RING_R = 14;
const CHOIR_STONES = 10;

/**
 * The in-code greybox dressing: what the zone is with no sidecar. Every box here has a
 * counterpart in `downs_region.py`, which is why a sidecar replaces this list wholesale.
 * Returns the *structural* boxes only; the seeded clutter scatter is added later, once the
 * waypoint graph exists to filter it against.
 */
function greyboxStructure(terrain: Heightfield): Aabb[] {
  const boxes: Aabb[] = [];

  // The Lantern Road: kerb segments along both edges of the flattened corridor, plus two gate
  // marker posts at the south threshold. Contiguous 60 m segments from z=250 down to z=-230.
  const KERB_Z_CENTERS = [220, 160, 100, 40, -20, -80, -140, -200];
  for (const z of KERB_Z_CENTERS) {
    boxes.push(box(-8.5, 0.15, z, 0.4, 0.3, 60), box(8.5, 0.15, z, 0.4, 0.3, 60));
  }
  boxes.push(box(-9, 1.25, 252, 0.4, 2.5, 0.4), box(9, 1.25, 252, 0.4, 2.5, 0.4));

  // The wayshrine (the zone's one graveyard): a plinth and a lantern post, just off the road at
  // roughly mid-zone.
  boxes.push(box(SHRINE_X, 0.3, SHRINE_Z, 2, 0.6, 2)); // plinth
  boxes.push(box(SHRINE_X, 0.6 + 1.25, SHRINE_Z, 0.3, 2.5, 0.3)); // lantern post, on the plinth

  // The Tithe Barrows (east): burial mounds, seeded so they read as natural, plus two entrance
  // stelae marking the approach off the road.
  boxes.push(
    ...scatter(
      11,
      8,
      { x0: 110, z0: 20, x1: 190, z1: 110 },
      [{ w: 10, d: 10, hMin: 2.5, hMax: 4 }],
      (x, z) => heightAt(terrain, x, z),
    ),
  );
  boxes.push(box(100, 1.5, 70, 0.4, 3, 0.4), box(100, 1.5, 50, 0.4, 3, 0.4));

  // The Choir Stones (centre-north): a ten-stone ring around a low altar.
  for (let i = 0; i < CHOIR_STONES; i++) {
    const a = (i / CHOIR_STONES) * Math.PI * 2;
    const sx = CHOIR_CX + Math.cos(a) * CHOIR_RING_R;
    const sz = CHOIR_CZ + Math.sin(a) * CHOIR_RING_R;
    boxes.push(box(sx, heightAt(terrain, sx, sz) + 1.5, sz, 0.8, 3, 0.8));
  }
  boxes.push(box(CHOIR_CX, heightAt(terrain, CHOIR_CX, CHOIR_CZ) + 0.4, CHOIR_CZ, 2, 0.8, 2));

  // The Drowned Chapel (low west): a roofless, part-broken ruin sitting in the dip, plus
  // scattered rubble. `yBase` is the terrain's own dip so the floor sits flush with the ground.
  const chapelFloorY = heightAt(terrain, CHAPEL_X, CHAPEL_Z);
  boxes.push(
    ...slabRoom(
      CHAPEL_X,
      CHAPEL_Z,
      16,
      12,
      3,
      0.4,
      [
        { wall: 'n', center: CHAPEL_X, width: 5 }, // broken north wall
        { wall: 'e', center: CHAPEL_Z, width: 4 }, // broken east wall
      ],
      { yBase: chapelFloorY, roof: false },
    ),
  );
  boxes.push(
    ...scatter(
      21,
      6,
      { x0: -172, z0: 8, x1: -148, z1: 32 },
      [{ w: 1.5, d: 1.5, hMin: 0.4, hMax: 1 }],
      (x, z) => heightAt(terrain, x, z),
    ),
  );

  // The Hollow Orchard (south-west): dead trees, seeded, plus two larger "hollow" trunks.
  boxes.push(
    ...scatter(
      31,
      16,
      { x0: -195, z0: 105, x1: -105, z1: 195 },
      [
        { w: 0.5, d: 0.5, hMin: 3, hMax: 6 },
        { w: 0.7, d: 0.7, hMin: 4, hMax: 7 },
      ],
      (x, z) => heightAt(terrain, x, z),
    ),
  );
  boxes.push(box(-150, heightAt(terrain, -150, 150) + 3.5, 150, 1.4, 7, 1.4));
  boxes.push(box(-130, heightAt(terrain, -130, 165) + 3, 165, 1.2, 6, 1.2));

  // The Watch Hill (north-east, the high point): a small tower shell with a south-facing door,
  // plus a scatter of boundary rocks around the summit.
  boxes.push(
    ...slabRoom(
      WATCH_HILL_X,
      WATCH_HILL_Z,
      6,
      6,
      8,
      0.4,
      [{ wall: 's', center: WATCH_HILL_X, width: 2.5 }],
      { yBase: heightAt(terrain, WATCH_HILL_X, WATCH_HILL_Z), roof: true },
    ),
  );
  boxes.push(
    ...scatter(
      41,
      6,
      { x0: 150, z0: -210, x1: 210, z1: -150 },
      [{ w: 1.5, d: 1.5, hMin: 1, hMax: 2 }],
      (x, z) => heightAt(terrain, x, z),
    ),
  );

  return boxes;
}

// ---------------------------------------------------------------------------------------------
// Clutter filter: a seeded prop that happened to land on a patrol/road line would be a rock the
// client draws but nothing walks through. Same rule spine.ts's scree filter uses.
// ---------------------------------------------------------------------------------------------
function segmentNearBoxXZ(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  b: Aabb,
  margin: number,
): boolean {
  const x0 = b.min.x - margin;
  const x1 = b.max.x + margin;
  const z0 = b.min.z - margin;
  const z1 = b.max.z + margin;
  const dx = bx - ax;
  const dz = bz - az;
  let t0 = 0;
  let t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  return (
    clip(-dx, ax - x0) && clip(dx, x1 - ax) && clip(-dz, az - z0) && clip(dz, z1 - az) && t0 <= t1
  );
}
const CLUTTER_MARGIN = 0.7;

export interface VigilDownsBuildOptions {
  /** A parsed region sidecar (region.ts). Omit for the in-code greybox. */
  region?: RegionSidecar;
}

function buildVigilDowns(opts: VigilDownsBuildOptions = {}): MapDef {
  const region = opts.region;
  const terrain: Heightfield = region?.heightfield ?? IN_CODE_TERRAIN;
  // With a sidecar the Blender pass is authoritative for every collider; without one the greybox
  // structure stands and the seeded clutter is appended after the waypoint graph exists.
  const boxes: Aabb[] = region
    ? region.boxes.map((b) => ({ min: b.min, max: b.max }))
    : greyboxStructure(terrain);

  /**
   * Ground/structure height at (x, z): the terrain, or the top of any box there, whichever is
   * higher (capped at `maxY`) - same helper spine.ts uses for every content position. The cap
   * matters at the Watch Hill tower: its footprint carries both a floor and a roof, and an
   * uncapped query would silently resolve to the roof instead of the floor an entity wants.
   */
  const at = (x: number, z: number, maxY = Infinity): Vec3 => ({
    x,
    y: groundHeightAt(x, z, boxes, 0, maxY, terrain),
    z,
  });
  const watchSummitY = heightAt(terrain, WATCH_HILL_X, WATCH_HILL_Z);

  // -------------------------------------------------------------------------------------------
  // Waypoint graph: a spine of nodes along the Lantern Road, a branch out to each of the five
  // off-road areas, and a 3-node patrol loop per camp. Every id used anywhere below is a value
  // returned by `addNode`, so patrol/edge indices are valid by construction.
  // -------------------------------------------------------------------------------------------
  const nodes: Vec3[] = [];
  const edges: [number, number][] = [];
  const addNode = (x: number, z: number): number => {
    nodes.push(at(x, z));
    return nodes.length - 1;
  };
  const link = (a: number, b: number): void => {
    edges.push([a, b]);
  };
  const chain = (ids: number[]): void => {
    for (let i = 0; i < ids.length - 1; i++) link(ids[i]!, ids[i + 1]!);
  };
  /** A closed 3-node patrol loop, linked into the graph at `hub`. */
  const campLoop = (pts: [number, number][], hub: number): number[] => {
    const ids = pts.map(([x, z]) => addNode(x, z));
    chain(ids);
    link(ids[ids.length - 1]!, ids[0]!); // close the loop
    link(hub, ids[0]!);
    return ids;
  };

  // --- The Lantern Road spine: south gate to the north end, near the Choir Stones. ---
  const spine = [230, 180, 130, 80, 30, -20, -70, -120, -170, -220].map((z) => addNode(0, z));
  chain(spine);

  // --- Branches off the spine to the four outlying areas (the Choir Stones sit on the spine). ---
  const barrowsBranch = [addNode(60, 75), addNode(120, 65), addNode(150, 55)];
  chain(barrowsBranch);
  link(spine[3]!, barrowsBranch[0]!);

  const chapelBranch = [addNode(-60, 40), addNode(-110, 30), addNode(-150, 22)];
  chain(chapelBranch);
  link(spine[4]!, chapelBranch[0]!);

  const orchardBranch = [addNode(-60, 140), addNode(-110, 148), addNode(-150, 150)];
  chain(orchardBranch);
  link(spine[2]!, orchardBranch[0]!);

  const watchHillBranch = [addNode(80, -160), addNode(140, -172), addNode(180, -174)];
  chain(watchHillBranch);
  link(spine[8]!, watchHillBranch[0]!);

  const choirNode = addNode(0, -68); // just south of the ring's edge (ring radius 14 at z=-66)
  link(spine[6]!, choirNode);

  // -------------------------------------------------------------------------------------------
  // Camps: 10, weakest near the gate (large z) to hardest at the Watch Hill (far north-east).
  // Each entry names its patrol loop's centre and the entities standing there; `patrol` on an
  // entity is that camp's loop (or its reverse, for variety), `patrol: []` is a sentry.
  // -------------------------------------------------------------------------------------------
  const hymnSpawns: HymnSpawnDef[] = [];
  const addHymn = (kind: number, pos: Vec3, patrol: number[] = []): void => {
    hymnSpawns.push({ kind, pos, patrol });
  };

  // Camp 1 - Gate Camp A (weakest: Choristers only, patrol split for a sentry/roamer mix).
  {
    const loop = campLoop(
      [
        [20, 200],
        [28, 208],
        [14, 212],
      ],
      spine[0]!,
    );
    addHymn(EntityKind.Chorister, at(20, 205), loop);
    addHymn(EntityKind.Chorister, at(24, 206), [...loop].reverse());
    addHymn(EntityKind.Chorister, at(16, 204));
  }

  // Camp 2 - Gate Camp B (Choristers only).
  {
    const loop = campLoop(
      [
        [-20, 170],
        [-28, 178],
        [-14, 182],
      ],
      spine[1]!,
    );
    addHymn(EntityKind.Chorister, at(-20, 175), loop);
    addHymn(EntityKind.Chorister, at(-24, 173));
    addHymn(EntityKind.Chorister, at(-16, 176));
  }

  // Camp 3 - Road Bend Camp (still Choristers only - the last of the weak, near-gate tier).
  {
    const loop = campLoop(
      [
        [18, 114],
        [26, 122],
        [10, 126],
      ],
      spine[2]!,
    );
    addHymn(EntityKind.Chorister, at(18, 120), loop);
    addHymn(EntityKind.Chorister, at(22, 118), [...loop].reverse());
    addHymn(EntityKind.Chorister, at(14, 122));
    addHymn(EntityKind.Chorister, at(20, 124));
  }

  // Camp 4 - Hollow Orchard Camp (mid-zone: Choristers + a Cantor).
  {
    const loop = campLoop(
      [
        [-130, 134],
        [-138, 144],
        [-122, 146],
      ],
      orchardBranch[1]!,
    );
    addHymn(EntityKind.Chorister, at(-130, 140), loop);
    addHymn(EntityKind.Chorister, at(-134, 136));
    addHymn(EntityKind.Cantor, at(-126, 142));
  }

  // Camp 5 - Barrows Camp (mid-zone: Choristers + a Cantor).
  {
    const loop = campLoop(
      [
        [115, 66],
        [123, 76],
        [107, 78],
      ],
      barrowsBranch[1]!,
    );
    addHymn(EntityKind.Chorister, at(115, 72), loop);
    addHymn(EntityKind.Chorister, at(111, 70));
    addHymn(EntityKind.Cantor, at(119, 74));
  }

  // Camp 6 - Deep Barrows Camp (heavier mid-zone: Choristers + two Cantors).
  {
    const loop = campLoop(
      [
        [160, 44],
        [168, 54],
        [152, 56],
      ],
      barrowsBranch[2]!,
    );
    addHymn(EntityKind.Chorister, at(160, 50), loop);
    addHymn(EntityKind.Chorister, at(156, 48));
    addHymn(EntityKind.Cantor, at(164, 52), [...loop].reverse());
    addHymn(EntityKind.Cantor, at(158, 46));
  }

  // Camp 7 - Drowned Chapel Camp (heavy: Choristers + two Cantors; kept south of the ruin's own
  // footprint so the entities don't spawn inside its walls).
  {
    const loop = campLoop(
      [
        [-160, 29],
        [-168, 39],
        [-152, 41],
      ],
      chapelBranch[2]!,
    );
    addHymn(EntityKind.Chorister, at(-160, 35), loop);
    addHymn(EntityKind.Chorister, at(-156, 33));
    addHymn(EntityKind.Cantor, at(-164, 37), [...loop].reverse());
    addHymn(EntityKind.Cantor, at(-158, 32));
  }

  // Camp 8 - Choir Stones Camp (heavy: Choristers + two Cantors, guarding the ring).
  {
    const loop = campLoop(
      [
        [0, -58],
        [8, -64],
        [-8, -64],
      ],
      choirNode,
    );
    addHymn(EntityKind.Chorister, at(0, -62), loop);
    addHymn(EntityKind.Chorister, at(4, -60));
    addHymn(EntityKind.Cantor, at(-4, -60));
    addHymn(EntityKind.Cantor, at(-2, -64), [...loop].reverse());
  }

  // Camp 9 - Watch Hill Approach (the hardest tier begins: a Sexton with a Chorister escort).
  {
    const loop = campLoop(
      [
        [95, -152],
        [103, -162],
        [87, -164],
      ],
      watchHillBranch[0]!,
    );
    addHymn(EntityKind.Sexton, at(95, -158));
    addHymn(EntityKind.Chorister, at(91, -156), loop);
    addHymn(EntityKind.Chorister, at(99, -160), [...loop].reverse());
  }

  // Camp 10 - Watch Hill Summit (hardest: a Sexton on the tower floor with a three-Chorister
  // escort patrolling outside; the tower's only door faces south, so the loop stays south of it).
  {
    const loop = campLoop(
      [
        [180, -172],
        [188, -182],
        [172, -182],
      ],
      watchHillBranch[2]!,
    );
    // Capped at the floor: uncapped, this point resolves to the tower's roof.
    addHymn(EntityKind.Sexton, at(WATCH_HILL_X, WATCH_HILL_Z, watchSummitY + 0.5));
    addHymn(EntityKind.Chorister, at(180, -176), loop);
    addHymn(EntityKind.Chorister, at(184, -178), [...loop].reverse());
    addHymn(EntityKind.Chorister, at(176, -178));
  }

  const waypoints: WaypointGraph = { nodes, edges };

  // -------------------------------------------------------------------------------------------
  // General terrain scatter, filtered clear of every waypoint edge's line. Only on the greybox
  // path: with a sidecar, `downs_region.py` has already placed and filtered its own clutter.
  // -------------------------------------------------------------------------------------------
  if (!region) {
    const clearOfEveryEdge = (prop: Aabb): boolean => {
      for (const [a, b] of edges) {
        const A = nodes[a]!;
        const B = nodes[b]!;
        const lo = Math.min(A.y, B.y) - 0.5;
        const hi = Math.max(A.y, B.y) + 2.0;
        if (prop.max.y < lo || prop.min.y > hi) continue;
        if (segmentNearBoxXZ(A.x, A.z, B.x, B.z, prop, CLUTTER_MARGIN)) return false;
      }
      return true;
    };
    const generalScatter = scatter(
      51,
      40,
      { x0: -240, z0: -240, x1: 240, z1: 240 },
      [
        { w: 1, d: 1, hMin: 0.5, hMax: 1 },
        { w: 1.5, d: 1.2, hMin: 0.8, hMax: 1.5 },
        { w: 2, d: 1.5, hMin: 1, hMax: 1.8 },
      ],
      (x, z) => heightAt(terrain, x, z),
    );
    boxes.push(...generalScatter.filter(clearOfEveryEdge));
  }

  const world: World = { groundY: 0, boxes, terrain };

  // -------------------------------------------------------------------------------------------
  // Named areas - exactly these six, exact names (the quest library resolves `areaHint` against
  // them). Generous `halfHeight` (26-32) so the whole vertical slice at each counts.
  // -------------------------------------------------------------------------------------------
  const areas: AreaDef[] = [
    { id: 1, name: 'the Lantern Road', pos: at(0, 130), radius: 55, halfHeight: 26 },
    { id: 2, name: 'the Tithe Barrows', pos: at(150, 60), radius: 55, halfHeight: 26 },
    { id: 3, name: 'the Choir Stones', pos: at(CHOIR_CX, CHOIR_CZ), radius: 45, halfHeight: 26 },
    { id: 4, name: 'the Drowned Chapel', pos: at(CHAPEL_X, CHAPEL_Z), radius: 50, halfHeight: 26 },
    { id: 5, name: 'the Hollow Orchard', pos: at(-150, 150), radius: 50, halfHeight: 26 },
    // Capped at the floor for the same reason as the Sexton's spawn above.
    {
      id: 6,
      name: 'the Watch Hill',
      pos: at(WATCH_HILL_X, WATCH_HILL_Z, watchSummitY + 0.5),
      radius: 65,
      halfHeight: 32,
    },
  ];

  // -------------------------------------------------------------------------------------------
  // NPCs at the wayshrine, clear of the plinth's 2x2 footprint (x 17..19, z 39..41).
  // -------------------------------------------------------------------------------------------
  const ottStand = region?.npcStands.find((s) => s.id === 'ott');
  const pellStand = region?.npcStands.find((s) => s.id === 'pell');
  const ottPos: Vec3 = ottStand?.pos ?? at(14, 44);
  const pellPos: Vec3 = pellStand?.pos ?? at(22, 37);
  const faceShrine = (p: Vec3): number => Math.atan2(-(SHRINE_X - p.x), -(SHRINE_Z - p.z));
  const npcs: NpcDef[] = [
    {
      id: 1,
      name: 'Deacon Ott',
      role: 'the wayshrine',
      // char-01: the same body he wears on the Spine.
      body: 'char-01',
      pos: ottPos,
      yaw: ottStand?.yaw ?? faceShrine(ottPos),
      bark: "Every one of them out there was a name once. I keep this shrine lit so the names don't run out first.",
      voice: 'ott',
    },
    {
      id: 2,
      name: 'Pell',
      role: 'the wayshrine',
      // char-03, scaled down: the same child body he has on the Spine.
      body: 'char-03',
      bodyScale: 0.78,
      pos: pellPos,
      yaw: pellStand?.yaw ?? faceShrine(pellPos),
      bark: "You get this far, you're already past the easy part. Touch the stone if you go down - it remembers where to send you back.",
      voice: 'pell',
    },
  ];

  // -------------------------------------------------------------------------------------------
  // Spawns: a 3x4 grid on the south gate apron, used only when someone logs in with no saved
  // position. Clear of the kerbs (x +/-8.5) and the gate posts (x +/-9, z=252) by construction.
  // -------------------------------------------------------------------------------------------
  const spawns: Vec3[] = [];
  for (const z of [248, 240, 232]) {
    for (const x of [-15, -5, 5, 15]) spawns.push(at(x, z));
  }

  const map: MapDef = {
    id: 'vigil-downs',
    name: 'the Vigil Downs',
    persistent: true,
    maxPlayers: 200,
    world,
    spawns,
    graveyards: [region?.graveyards[0] ?? at(SHRINE_X, SHRINE_Z + 3)],
    // The danger gradient the camps above are already laid out for: level 1 at the gate, level 10
    // at the Watch Hill. Banded by distance from `spawns[0]`, so moving a camp moves its level.
    levelRange: [1, 10],
    /**
     * The one way back to Lanternfall. `region` sits on the south gate apron, just inside the
     * 500 m edge (z=250).
     *
     * `spawn` (0, 0, -35) is Lanternfall's own arrival point, 25 m south of that city's gate wall
     * and 19 m clear of Lanternfall's exit-to-here region's edge (25 - radius 6 = 19 >= the 12 m
     * minimum) - see lanternfall.ts's matching exit. Lanternfall's exit lands players here at
     * (0, 0, 215) - the Lantern Road, just inside this map's own gate - which is likewise 24 m
     * clear of this region's own edge (30 - radius 6 = 24), so arriving players never immediately
     * re-trigger either exit.
     */
    exits: [
      {
        id: 1,
        name: 'Lanternfall',
        to: 'lanternfall',
        region: { pos: { x: 0, y: 0, z: 245 }, radius: 6, halfHeight: 6 },
        spawn: { x: 0, y: 0, z: -35 },
        yaw: Math.PI, // south, into the city
      },
    ],
    defaultAreaName: 'the Vigil Downs',
    areas,
    npcs,
    hymnSpawns,
    waypoints,
  };
  if (region) map.regionBoxes = boxes;
  return map;
}

/**
 * The Vigil Downs. A singleton, for the same reason `SPINE` is: `MAPS`, every room, the bots' nav
 * cache and the client all hold this object, so `applyDownsRegion` rebuilds it *in place*.
 */
export const VIGIL_DOWNS: MapDef = buildVigilDowns();

/**
 * Re-authors `VIGIL_DOWNS` against a region sidecar (or back to the greybox with `null`). Call it
 * once at startup, before any room or prediction exists - a room that captured
 * `VIGIL_DOWNS.world` earlier keeps colliding against the old terrain.
 */
export function applyDownsRegion(region: RegionSidecar | null): MapDef {
  const next = buildVigilDowns(region ? { region } : {});
  delete VIGIL_DOWNS.regionBoxes;
  Object.assign(VIGIL_DOWNS, next);
  return VIGIL_DOWNS;
}
